import { ICell, ICellMetricUOM, ICubeSource, ICubeSourceCellOperation } from './PA.HyperRectangle.IF'
import * as _ from 'lodash'
import { IMessageProvider, IProgressProvider, MessageType } from '../PA.Core/PA.Core.IF.Basic'
import { Index } from '../PA.Core/Basic/Index'
import { ErrorMessage_restate, WarningMessage_restate } from '../PA.Core/Basic/MessageUtils'
import { UOM } from '../PA.MD.Services/PA.MD.Services.IF'
import { DimensionType } from '../PA.Core/PA.Core.IF.Hierarchy'
import { PerspectiveLocalCell } from './PerspectiveLocalCell'
import { PASearch } from '../PA.Core/PASearch'

export interface ICellOperationOptions {
    progressProvider?: IProgressProvider
    messageProvider?: IMessageProvider
}

export class CubeSourceUtils {
    static async executeOperation(
        cubeSource: ICubeSource,
        cellOp: ICubeSourceCellOperation,
        opts?: ICellOperationOptions
    ): Promise<boolean> {
        //
        if (_.isEmpty(cubeSource)) return false
        const progressProvider = opts?.progressProvider
        const messageProvider = opts?.messageProvider
        let tgtCellCount = 0
        let tgtCellMiss = 0
        let tgtMetricSetCount = 0

        let opName = 'Copy'
        if (cellOp.Operation == 'MetricOperation') {
            switch (cellOp.SubOperation) {
                case 'SetTo':
                    opName = 'Setting Value'
                    break
                case 'ScaleBy':
                    opName = 'Scale Value'
                    break
                case 'Reallocate':
                    opName = 'Reallocate Value'
            }
        }

        if (progressProvider) progressProvider.start(opName)

        try {
            // Add Metrics to Perspective Options
            const allMetrics = _.clone(cellOp.CubePerspectiveOptions.MetricIds || [])
            const dctMetrics = _.keyBy(allMetrics, (a) => a)
            let arrMetricIds: string[]
            if (cellOp.Operation == 'MetricOperation') {
                arrMetricIds = [...cellOp.TargetMetrics]
            } else if (cellOp.Operation == 'Metric') {
                arrMetricIds = [cellOp.SourceMetric, ...cellOp.TargetMetrics]
            } else {
                arrMetricIds = cellOp.CopyMetrics || []

                // Add Dimension Filter for Source and Target Nodes
                let filterByNodes = [cellOp.SourceNode, ...(cellOp.TargetNodes || [])]
                cellOp.CubePerspectiveOptions.DimensionFilters[cellOp.Operation] = { FilterByNodes: filterByNodes }
            }
            _.forEach(arrMetricIds, (aMetricId) => {
                if (!(aMetricId in dctMetrics)) {
                    allMetrics.push(aMetricId)
                    dctMetrics[aMetricId] = aMetricId
                }
            })
            cellOp.CubePerspectiveOptions.MetricIds = allMetrics

            const perspective = cubeSource.createPerspective('Operation', cellOp.CubePerspectiveOptions)
            await perspective.OnCubePerspectiveLoadComplete

            const idxOrder = perspective.getDimensions()
            let bUsePeriodOffsetLogic: boolean =
                cellOp.Operation == 'Metric' && cellOp.OffsetPeriods > 0 && _.includes(idxOrder, DimensionType.Time)
            const idxIndexers: ((t: ICell) => string)[] = []
            _.forEach(idxOrder, (dimType) => {
                idxIndexers.push((aCell) => aCell.getKey(dimType))
            })
            const idx = new Index<ICell>(idxIndexers)
            _.forEach(perspective.Cells, (a) => idx.add(a))

            const dctCellPeriodSrc: { [id: string]: string | number } = {}
            let periodIds: string[] = []
            let dctOffsetPeriodIdBySrcId: { [srcPeriodId: string]: string } = {} // doesn't wrap
            let cellPeriodIds: string[] = []
            let idxMissingTgtCells = new Index<ICell>(idxIndexers)

            if (bUsePeriodOffsetLogic) {
                const periodHierarchy = cubeSource.getDimensionHierarchy(DimensionType.Time)
                if (_.isEmpty(periodHierarchy)) {
                    bUsePeriodOffsetLogic = false
                } else {
                    const periodLevelId = cellOp.CubePerspectiveOptions.Dimensions['Period'].LevelId
                    let periodHierarchyNodes = periodHierarchy.getNodesAtLevel(periodLevelId)
                    periodHierarchyNodes = _.sortBy(periodHierarchyNodes, (a) => a.NodeOrder)
                    periodIds = _.map(periodHierarchyNodes, (a) => a.NodeId)
                    _.forEach(periodIds, (srcPeriodId, idx) => {
                        // Offset is "Move back" direction
                        let offsetIdx = +idx - cellOp.OffsetPeriods
                        let tgtPeriodId = periodIds[offsetIdx]
                        if (!_.isEmpty(tgtPeriodId)) dctOffsetPeriodIdBySrcId[srcPeriodId] = tgtPeriodId
                    })

                    cellPeriodIds = _.flatMap(perspective.Cells, (aCell) => aCell.getKey('Period'))
                    cellPeriodIds = _.uniq(cellPeriodIds)
                    cellPeriodIds = _.intersection(periodIds, cellPeriodIds) // Sorted by first array

                    // Find Target Periods
                    let tgtPeriodIds: string[] = []
                    _.forEach(cellPeriodIds, (srcPeriodId) => {
                        if (srcPeriodId in dctOffsetPeriodIdBySrcId)
                            tgtPeriodIds.push(dctOffsetPeriodIdBySrcId[srcPeriodId])
                    })
                    let missingTgtPeriodIds = _.difference(tgtPeriodIds, cellPeriodIds)
                    if (!_.isEmpty(missingTgtPeriodIds)) {
                        // Use CubeSource to get missing target cells
                        let tgtPerspOpts = _.cloneDeep(cellOp.CubePerspectiveOptions)
                        tgtPerspOpts.DimensionFilters.Period = {
                            FilterByNodes: _.map(missingTgtPeriodIds, (id) => periodHierarchy.getNode(id))
                        }

                        const tgtPerspective = cubeSource.createPerspective('OperationTgt', tgtPerspOpts)
                        await tgtPerspective.OnCubePerspectiveLoadComplete

                        _.forEach(tgtPerspective.Cells, (aCell) => {
                            idxMissingTgtCells.add(aCell)
                        })
                    }

                    // Populate Source Value Dictionary
                    _.forEach(perspective.Cells, (aCell) => {
                        let idxVals = _.map(idxOrder, (dimType) => aCell.getKey(dimType))
                        let srcVal = aCell.getCellMetricValue(cellOp.SourceMetric)
                        _.set(dctCellPeriodSrc, idxVals, srcVal)
                    })
                }
            }

            //  If across Metrics, copy across
            if (cellOp.Operation == 'Metric') {
                if (bUsePeriodOffsetLogic) {
                    let periodIdxPos = _.indexOf(idxOrder, DimensionType.Time)
                    _.forEach(perspective.Cells, (aCell) => {
                        tgtCellCount++
                        let srcIdxVals = _.map(idxOrder, (dimType) => aCell.getKey(dimType))
                        // Determine Target Dimension Keys
                        let tgtIdxVals = _.clone(srcIdxVals)

                        const srcPeriodId = aCell.getKey('Period')
                        const tgtPeriodId = dctOffsetPeriodIdBySrcId[srcPeriodId]

                        if (_.isEmpty(tgtPeriodId)) {
                            return
                        }
                        tgtIdxVals[periodIdxPos] = tgtPeriodId

                        // Get the saved Original Value
                        const metricValue = _.get(dctCellPeriodSrc, srcIdxVals)
                        if (_.isNaN(metricValue)) return

                        // Find the Target Either from the Source Index or Target Missing Idx
                        let tgtCell = <ICell>idx.get(tgtIdxVals)
                        if (_.isEmpty(tgtCell)) tgtCell = <ICell>idxMissingTgtCells.get(tgtIdxVals)
                        if (_.isEmpty(tgtCell)) return

                        _.forEach(cellOp.TargetMetrics || [], (aTargetMetricId) => {
                            tgtCell.setCellMetricValue(aTargetMetricId, metricValue)
                            tgtMetricSetCount++
                        })
                    })
                } else {
                    _.forEach(perspective.Cells, (aCell) => {
                        tgtCellCount++
                        const metricValue = aCell.getCellMetricValue(cellOp.SourceMetric)
                        if (_.isNaN(metricValue)) return

                        _.forEach(cellOp.TargetMetrics || [], (aTargetMetricId) => {
                            aCell.setCellMetricValue(aTargetMetricId, metricValue)
                            tgtMetricSetCount++
                        })
                    })
                }
            }
            else if (cellOp.Operation == 'MetricOperation') {
                let allocateRule: number[] = []
                let splitWeeks: PerspectiveLocalCell[] = []

                let tgtValue = cellOp.OperationParam1
                _.forEach(perspective.Cells, (aCell) => {
                    if (_.includes(['SetTo', 'ScaleBy'], cellOp.SubOperation)) tgtCellCount++

                    _.forEach(cellOp.TargetMetrics || [], (aTargetMetricId) => {
                        if (_.isNaN(tgtValue)) return

                        switch (cellOp.SubOperation) {
                            case 'SetTo':
                                aCell.setCellMetricValue(aTargetMetricId, tgtValue)
                                break
                            case 'ScaleBy':

                                let newValue = tgtValue * <number>aCell.getCellMetricValue(aTargetMetricId)
                                aCell.setCellMetricValue(aTargetMetricId, newValue)
                                break
                            case 'Reallocate':
                                let pCell = <PerspectiveLocalCell>aCell
                                // @ts-ignore
                                if (pCell._cells.length > 1) {
                                    splitWeeks.push(pCell)
                                }
                                break
                            default:
                                return
                        }
                        tgtMetricSetCount++
                    })
                })

                if (cellOp.SubOperation == 'Reallocate' && splitWeeks.length > 0) {
                    tgtCellCount = splitWeeks.length * 2
                    let cellPeriodIds = _.flatMap(splitWeeks, (aCell) => aCell.getKey('Period')).map(Number)
                    if ('ReallocationRule' in cellOp && 'WeekDays' in cellOp.ReallocationRule) {
                        for (let dow of cellOp.ReallocationRule.WeekDays.split(',')) {
                            switch (dow) {
                                case 'Monday':
                                    allocateRule.push(2)
                                    break
                                case 'Tuesday':
                                    allocateRule.push(3)
                                    break
                                case 'Wednesday':
                                    allocateRule.push(4)
                                    break
                                case 'Thursday':
                                    allocateRule.push(5)
                                    break
                                case 'Friday':
                                    allocateRule.push(6)
                                    break
                                case 'Saturday':
                                    allocateRule.push(7)
                                    break
                                case 'Sunday':
                                    allocateRule.push(1)
                                    break
                            }
                        }
                    }
                    await (async () => {
                        try {
                            let arrSplit = await PASearch.find(
                                'vUI_PERIOD_CHILDREN',
                                { WB_PERIOD_ID_PRNT: { inq: cellPeriodIds } },
                                { WB_PERIOD_ID_PRNT: { type: 'int' } },
                                [],
                                0,
                                []
                            )

                            let results = _.groupBy(arrSplit, 'WB_PERIOD_ID_PRNT')

                            let aTargetMetricId = _.first(cellOp.TargetMetrics)

                            for (let planningWeek of splitWeeks) {
                                let tgtValue = <number>planningWeek.getCellMetricValue(aTargetMetricId)
                                let dctCells = _.keyBy(results[Number(planningWeek.getKey('Period'))], 'WB_PERIOD_ID')
                                // @ts-ignore
                                for (let cell of planningWeek._cells) {
                                    let cellRange = dctCells[Number(cell.getKey('Period'))]
                                    if (!_.isEmpty(allocateRule)) {
                                        let cnt = _.filter(
                                            allocateRule,
                                            (a) => a >= +cellRange.START_DOW && a <= +cellRange.END_DOW
                                        ).length
                                        cell.setCellMetricValue(aTargetMetricId, (tgtValue * cnt) / allocateRule.length)
                                    } else {
                                        cell.setCellMetricValue(
                                            aTargetMetricId,
                                            (tgtValue * cellRange.T_DURATION) / cellRange.P_DURATION
                                        )
                                    }
                                }
                                await planningWeek.refreshMetricValues([aTargetMetricId])
                            }
                        } catch (e) {
                            console.error(e)
                        }
                    })()
                }
            } else {
                const srcDimKey = cellOp.SourceNode.NodeId
                const srcDimType = cellOp.Operation
                const dctTgtDimKey = _.keyBy(cellOp.TargetNodes, (n) => n.NodeId)

                const copyFromCells: ICell[] = []
                const copyToCells: ICell[] = []
                _.forEach(perspective.Cells, (aCell) => {
                    const cellKey = aCell.getKey(srcDimType)
                    if (cellKey == srcDimKey) copyFromCells.push(aCell)
                    else if (cellKey in dctTgtDimKey) {
                        copyToCells.push(aCell)
                    }
                })

                // create index for target cells
                const idxOrder = cubeSource.getDimensions()
                const idxIndexers: ((t: ICell) => string)[] = []
                _.forEach(idxOrder, (dimType) => {
                    idxIndexers.push((aCell) => aCell.getKey(dimType))
                })
                const idx = new Index<ICell>(idxIndexers)
                _.forEach(copyToCells, (aCell) => {
                    idx.add(aCell)
                })

                _.forEach(copyFromCells, (aSrcCell) => {
                    _.forEach(cellOp.TargetNodes, (aTgtNode) => {
                        const tgtKeys: string[] = []
                        _.forEach(idxOrder, (aDimType) => {
                            if (aDimType == srcDimType) {
                                tgtKeys.push(aTgtNode.NodeId)
                            } else {
                                // Use the key from the source
                                tgtKeys.push(aSrcCell.getKey(aDimType))
                            }
                        })

                        const tgtCell: ICell = <ICell>idx.get(tgtKeys)

                        if (tgtCell) {
                            tgtCellCount++
                            _.forEach(cellOp.CopyMetrics, (aMetricId) => {
                                const srcValue = aSrcCell.getCellMetricValue(aMetricId)
                                if (_.isNumber(srcValue)) {
                                    tgtCell.setCellMetricValue(aMetricId, srcValue)
                                    tgtMetricSetCount++
                                }
                            })
                        } else {
                            //-- No target
                            tgtCellMiss++
                        }
                    })
                })
            }

            if (messageProvider)
                messageProvider.addMessage({
                    type: MessageType.Information,
                    code: 'Stats',
                    title: `${opName} Completed, Updated ${tgtCellCount} cells.`,
                    description: `Updated ${tgtCellCount} cells, ${tgtMetricSetCount} values.`
                })

            if (progressProvider) progressProvider.finish()

            return true
        } catch (err) {
            if (messageProvider)
                messageProvider.addMessage({
                    type: MessageType.Information,
                    code: 'Failed',
                    title: `${opName} Failed`,
                    description: `Error: ${JSON.stringify(err, null, '\t')}`
                })

            if (progressProvider) progressProvider.exception(err)

            return false
        }
    }

    static validateOperation(
        cubeSource: ICubeSource,
        cellOp: ICubeSourceCellOperation,
        opts?: ICellOperationOptions
    ): boolean {
        let returnValue = true
        //--
        const bNoCubeSource = _.isEmpty(cubeSource)
        const bNoCellOperation = _.isEmpty(cellOp)
        ErrorMessage_restate(opts.messageProvider, bNoCubeSource, 'NoCubeSource', 'No CubeSource')
        ErrorMessage_restate(opts.messageProvider, bNoCellOperation, 'NoCellOperation', 'No Cell Operation')

        if (bNoCubeSource || bNoCellOperation) return false

        // Remove all Errors, by Id?
        if (opts.messageProvider)
            _.forEach(
                [
                    'NoOperationType',
                    'UnknownCrossDimension',
                    'NoSourceMetric',
                    'NoTargetMetrics',
                    'OverlapTargetNodes',
                    'SourceTargetMetrics',
                    'NoSubOperationType',
                    'NoOperationParameter',
                    'NoSourceNode',
                    'NoTargetNodes',
                    'NoMetricsSelected'
                ],
                (errCd) => {
                    opts.messageProvider.removeMessage(errCd)
                }
            )

        //--
        let arrDims = _.map(cubeSource.getDimensions(), (a) => cubeSource.getDimensionHierarchy(a))
        arrDims = _.orderBy(arrDims, 'DimensionType', 'asc')
        const dctDims = _.keyBy(arrDims, (a) => a.DimensionType)
        if (cellOp.Operation == 'Metric') {
            let bCheckUOM = false
            let sourceUOM: ICellMetricUOM = undefined
            if (_.isEmpty(cellOp.SourceMetric)) {
                WarningMessage_restate(opts.messageProvider, true, 'NoSourceMetric', `Select Source Metric`)
                returnValue = false
            } else {
                if (_.isFunction(cubeSource.getCellMetricBaseUOM)) {
                    sourceUOM = cubeSource.getCellMetricBaseUOM(cellOp.SourceMetric)
                    if (!_.isEmpty(sourceUOM)) bCheckUOM = true
                }
            }

            if (_.isEmpty(cellOp.TargetMetrics)) {
                WarningMessage_restate(opts.messageProvider, true, 'NoTargetMetrics', `Select Target Metrics`)
                returnValue = false
            }
            _.forEach(cellOp.TargetMetrics, (tgtMtrcId) => {
                if (cellOp.SourceMetric == tgtMtrcId) {
                    WarningMessage_restate(
                        opts.messageProvider,
                        true,
                        'SourceTargetMetrics',
                        `Remove Source Metric from Target Metrics`
                    )
                    returnValue = false
                }
                // Check UOM compatibility
                if (bCheckUOM) {
                    const tgtUOM = cubeSource.getCellMetricBaseUOM(tgtMtrcId)
                    if (!CubeSourceUtils.isUOMCopyCompatible(sourceUOM, tgtUOM)) {
                        WarningMessage_restate(
                            opts.messageProvider,
                            true,
                            `UOMMismatch${cellOp.SourceMetric}${tgtMtrcId}`,
                            `UOM mismatch for Target Metric ${tgtMtrcId}`
                        )
                        returnValue = false
                    }
                }
            })
        } else if (cellOp.Operation == 'MetricOperation') {
            if (_.isEmpty(cellOp.SubOperation)) {
                WarningMessage_restate(opts.messageProvider, true, 'NoSubOperationType', `Select Operation Type`)
                returnValue = false
            }
            if (!_.isNumber(cellOp.OperationParam1)) {
                WarningMessage_restate(opts.messageProvider, true, 'NoOperationParameter', `Select Operation Parameter`)
                returnValue = false
            }

            if (_.isEmpty(cellOp.TargetMetrics)) {
                WarningMessage_restate(opts.messageProvider, true, 'NoTargetMetrics', `Select Target Metrics`)
                returnValue = false
            }
        } else {
            if (!(cellOp.Operation in dctDims)) {
                ErrorMessage_restate(
                    opts.messageProvider,
                    true,
                    'UnknownCrossDimension',
                    `Unknown Cross Dimension ${cellOp.Operation}`
                )
                return false
            }
            const crossHier = dctDims[cellOp.Operation]

            if (_.isEmpty(cellOp.SourceNode)) {
                WarningMessage_restate(opts.messageProvider, true, 'NoSourceNode', `Select Source Node`)
                returnValue = false
            }

            if (_.isEmpty(cellOp.TargetNodes)) {
                WarningMessage_restate(opts.messageProvider, true, 'NoTargetNodes', `Select Target Nodes`)
                returnValue = false
            }

            if (!(_.isEmpty(cellOp.SourceNode) || _.isEmpty(cellOp.TargetNodes))) {
                const nodeId = cellOp.SourceNode.NodeId
                const bExists = !_.isEmpty(_.filter(cellOp.TargetNodes, (a) => nodeId == a.NodeId))
                if (bExists) {
                    WarningMessage_restate(
                        opts.messageProvider,
                        true,
                        'OverlapTargetNodes',
                        `Source Node in Target List, ${cellOp.SourceNode.NodeName} (${cellOp.SourceNode.NodeIdUI})`
                    )
                    returnValue = false
                }
            }

            if (_.isEmpty(cellOp.CopyMetrics)) {
                WarningMessage_restate(opts.messageProvider, true, 'NoMetricsSelected', `Select Metrics to Copy`)
                returnValue = false
            }
        }

        // Validate the Dimensions
        const pDims = cellOp?.CubePerspectiveOptions?.Dimensions || {}

        for (const dimHier of arrDims) {
            const dimType = dimHier.DimensionType
            let sDimLevel = ''
            if (pDims && pDims[dimType]) sDimLevel = pDims[dimType].LevelId
            else continue

            if (_.isEmpty(sDimLevel)) {
                WarningMessage_restate(
                    opts.messageProvider,
                    true,
                    `NoDimLevel${dimType}`,
                    `Select Level for ${dimType}`
                )
                returnValue = false
            }
        }

        return returnValue
    }

    static isUOMCopyCompatible(a: ICellMetricUOM, b: ICellMetricUOM): boolean {
        if (_.isEmpty(a) && _.isEmpty(b)) return true

        if (_.isEmpty(a) && !_.isEmpty(b)) return false

        if (_.isEmpty(b) && !_.isEmpty(a)) return false

        function uomCompat(x: UOM, y: UOM): boolean {
            if (_.isEmpty(x) && _.isEmpty(y)) return true

            if (_.isEmpty(x) && !_.isEmpty(y)) return false

            if (_.isEmpty(y) && !_.isEmpty(x)) return false

            if (x.bUOM != y.bUOM) return false

            if (x.bCurrency != y.bCurrency) return false

            if (x.bUOM && x.UOMCode != y.UOMCode) return false

            if (x.bCurrency && x.CurrencyCode != y.CurrencyCode) return false

            return true
        }

        return uomCompat(a?.NumeratorUOM, b?.NumeratorUOM) && uomCompat(a?.DenominatorUOM, b?.DenominatorUOM)
    }
}
